import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaEye } from 'react-icons/fa';
import { getModelAuditLogs } from '../../../redux/Slices/AuditLogSlice';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../shared/hooks/TitleContext';
import i18n from '../../../i18n/i18n';

const ModelAuditLogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { modelType, modelId } = useParams();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setTitle('auditLogs.modelLogsTitle', { defaultValue: 'سجلات التحركات للنموذج' });
    if (modelType && modelId) {
      fetchModelLogs();
    }
  }, [modelType, modelId, setTitle]);

  const fetchModelLogs = async () => {
    setIsLoading(true);
    try {
      const result = await dispatch(getModelAuditLogs({ 
        modelType: decodeURIComponent(modelType), 
        modelId, 
        language: i18n.language 
      }));
      if (getModelAuditLogs.fulfilled.match(result)) {
        const data = result.payload?.data || result.payload;
        const logs = Array.isArray(data) ? data : (data?.data || []);
        setAuditLogs(logs);
      } else {
        toast.error(result.payload || t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجلات' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(t('auditLogs.errors.fetchError', { defaultValue: 'فشل في جلب السجلات' }), { rtl: isRTL });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventLabel = (eventType) => {
    const labels = {
      created: isRTL ? 'إنشاء' : 'Created',
      updated: isRTL ? 'تحديث' : 'Updated',
      deleted: isRTL ? 'حذف' : 'Deleted',
      restored: isRTL ? 'استعادة' : 'Restored',
    };
    return labels[eventType] || eventType;
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
        {t('auditLogs.loading', { defaultValue: 'جاري التحميل...' })}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', direction: dir, textAlign }}>
      <div style={{ 
        backgroundColor: '#1b3350', 
        borderRadius: '12px', 
        padding: '24px',
        border: '1px solid #333'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ color: 'white', margin: 0 }}>
            {t('auditLogs.modelLogsTitle', { defaultValue: 'سجلات التحركات' })} - {decodeURIComponent(modelType)} #{modelId}
          </h2>
          <button
            onClick={() => navigate('/audit-logs')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FaArrowLeft /> {t('auditLogs.back', { defaultValue: 'رجوع' })}
          </button>
        </div>

        {auditLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#ccc' }}>
            {t('auditLogs.noData', { defaultValue: 'لا توجد بيانات' })}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
              <thead>
                <tr style={{ backgroundColor: '#AC2000' }}>
                  <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                    {t('auditLogs.id', { defaultValue: 'ID' })}
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                    {t('auditLogs.user', { defaultValue: 'المستخدم' })}
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                    {t('auditLogs.event', { defaultValue: 'الحدث' })}
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                    {t('auditLogs.createdAt', { defaultValue: 'تاريخ الإنشاء' })}
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                    {t('auditLogs.actions', { defaultValue: 'الإجراءات' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                      {log.id}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                      {log.user?.name || log.user?.email || '—'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                      {getEventLabel(log.event)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', color: '#ddd', border: '1px solid #333' }}>
                      {formatDate(log.created_at)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #333' }}>
                      <button
                        onClick={() => navigate(`/audit-logs/${log.id}`)}
                        style={{
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          margin: '0 4px'
                        }}
                        title={t('auditLogs.view', { defaultValue: 'عرض' })}
                      >
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelAuditLogs;
